import { Router } from "express";
import { z } from "zod";
import { and, asc, count, desc, eq, like, sql, type SQL } from "drizzle-orm";
import { db } from "../../db/index";
import { holidays, teams, designations, appreciations, awards, promotions, complianceItems } from "../../db/schema/hr";
import { employeeDetails } from "../../db/schema/employee";
import { users } from "../../db/schema/identity";
import { alias } from "drizzle-orm/mysql-core";
import { requireAuth, requirePermission, scopeCondition, SCOPE } from "../../rbac/index";

/**
 * HR satellite panes. Holidays first (V2 HolidaysController index L17–71 + detail L81–107; writes
 * from the legacy HolidayController). Company-scoped; writes gate on ADD/EDIT/DELETE=ALL. The
 * applies-to scope (departments/designations/employment-types) is stored as JSON arrays — empty = all.
 */
export const hrRouter = Router();
hrRouter.use(requireAuth);

const onlyAll = (perm: string) => (req: import("express").Request, res: import("express").Response, next: import("express").NextFunction) =>
  req.scope === SCOPE.ALL ? next() : res.status(403).json({ error: "Forbidden", permission: perm });

const todayStr = () => new Date().toISOString().slice(0, 10);
const parseJsonArr = (s: string | null): unknown[] => {
  if (!s) return [];
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; } catch { return []; }
};

/* ───────────────────────────── Holidays ───────────────────────────── */

/** GET /api/holidays — list by year + search + sort, with KPIs (legacy L17–71). */
hrRouter.get("/holidays", requirePermission("view_holiday"), async (req, res) => {
  const companyId = req.auth!.companyId;
  const year = Number(req.query.year) || new Date().getFullYear();
  const search = (req.query.search as string | undefined)?.trim() ?? "";
  const sort = (req.query.sort as string) === "occasion" ? holidays.occassion : holidays.date;
  const dir = (req.query.dir as string) === "desc" ? desc : asc;
  const yStart = `${year}-01-01`;
  const yEnd = `${year}-12-31`;

  const conds: SQL[] = [eq(holidays.companyId, companyId), sql`${holidays.date} >= ${yStart}`, sql`${holidays.date} <= ${yEnd}`];
  if (search) conds.push(like(holidays.occassion, `%${search}%`));

  const rows = await db
    .select({
      id: holidays.id, date: holidays.date, occasion: holidays.occassion,
      departmentIdJson: holidays.departmentIdJson, designationIdJson: holidays.designationIdJson, employmentTypeJson: holidays.employmentTypeJson,
    })
    .from(holidays)
    .where(and(...conds))
    .orderBy(dir(sort));

  const today = todayStr();
  const asDateStr = (v: unknown): string => (v instanceof Date ? v.toISOString().slice(0, 10) : String(v).slice(0, 10));
  const list = rows.map((h) => {
    const dep = parseJsonArr(h.departmentIdJson) as number[];
    const des = parseJsonArr(h.designationIdJson) as number[];
    const emp = parseJsonArr(h.employmentTypeJson) as string[];
    return {
      id: h.id,
      date: asDateStr(h.date),
      occasion: h.occasion ?? "",
      departmentIds: dep,
      designationIds: des,
      employmentTypes: emp,
      appliesToAll: dep.length === 0 && des.length === 0 && emp.length === 0,
    };
  });
  const upcoming = list.filter((h) => h.date >= today);
  res.json({
    rows: list,
    kpis: {
      thisYear: list.length,
      upcoming: upcoming.length,
      past: list.length - upcoming.length,
      next: upcoming.length ? upcoming.reduce((a, b) => (a.date <= b.date ? a : b)) : null,
    },
    years: [year - 2, year - 1, year, year + 1],
  });
});

const holidayBody = z.object({
  date: z.string().min(1),
  occasion: z.string().trim().max(100).optional(),
  department_ids: z.array(z.coerce.number().int()).default([]),
  designation_ids: z.array(z.coerce.number().int()).default([]),
  employment_types: z.array(z.string()).default([]),
});

/** POST /api/holidays — create (gate add_holiday=ALL). */
hrRouter.post("/holidays", requirePermission("add_holiday"), onlyAll("add_holiday"), async (req, res) => {
  const p = holidayBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [r] = await db.insert(holidays).values({
    companyId: req.auth!.companyId,
    date: new Date(p.data.date),
    occassion: p.data.occasion ?? null,
    departmentIdJson: JSON.stringify(p.data.department_ids),
    designationIdJson: JSON.stringify(p.data.designation_ids),
    employmentTypeJson: JSON.stringify(p.data.employment_types),
    addedBy: req.auth!.userId,
  }).$returningId();
  res.status(201).json({ id: r!.id, message: "Holiday added" });
});

/** PUT /api/holidays/:id — update (gate edit_holiday=ALL). */
hrRouter.put("/holidays/:id", requirePermission("edit_holiday"), onlyAll("edit_holiday"), async (req, res) => {
  const id = Number(req.params.id);
  const p = holidayBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [exists] = await db.select({ id: holidays.id }).from(holidays).where(and(eq(holidays.id, id), eq(holidays.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.update(holidays).set({
    date: new Date(p.data.date),
    occassion: p.data.occasion ?? null,
    departmentIdJson: JSON.stringify(p.data.department_ids),
    designationIdJson: JSON.stringify(p.data.designation_ids),
    employmentTypeJson: JSON.stringify(p.data.employment_types),
    lastUpdatedBy: req.auth!.userId,
  }).where(eq(holidays.id, id));
  res.json({ id, message: "Holiday updated" });
});

/** DELETE /api/holidays/:id (gate delete_holiday=ALL). */
hrRouter.delete("/holidays/:id", requirePermission("delete_holiday"), onlyAll("delete_holiday"), async (req, res) => {
  const id = Number(req.params.id);
  const [exists] = await db.select({ id: holidays.id }).from(holidays).where(and(eq(holidays.id, id), eq(holidays.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.delete(holidays).where(eq(holidays.id, id));
  res.json({ id, message: "Holiday deleted" });
});

/** GET /api/holidays/applies-options — departments + designations + employment types for the picker. */
hrRouter.get("/holidays/applies-options", requirePermission("view_holiday"), async (req, res) => {
  const companyId = req.auth!.companyId;
  const [depts, desigs] = await Promise.all([
    db.select({ id: teams.id, name: teams.teamName }).from(teams).where(eq(teams.companyId, companyId)).orderBy(asc(teams.teamName)),
    db.select({ id: designations.id, name: designations.name }).from(designations).where(eq(designations.companyId, companyId)).orderBy(asc(designations.name)),
  ]);
  res.json({ departments: depts, designations: desigs, employmentTypes: ["Full-time", "Part-time", "Contract", "Internship"] });
});

/* ───────────────────────────── Appreciations ───────────────────────────── */

/** GET /api/appreciations — list by year + search + sort, with KPIs (legacy V2 AppreciationsController L20–63). */
hrRouter.get("/appreciations", requirePermission("view_appreciation"), async (req, res) => {
  const companyId = req.auth!.companyId;
  const year = Number(req.query.year) || new Date().getFullYear();
  const search = (req.query.search as string | undefined)?.trim() ?? "";
  const sortKey = (req.query.sort as string) || "award_date";
  const dir = (req.query.dir as string) === "asc" ? asc : desc;
  const giver = alias(users, "giver");
  const yStart = `${year}-01-01`;
  const yEnd = `${year}-12-31`;

  const conds: SQL[] = [eq(appreciations.companyId, companyId), sql`${appreciations.awardDate} >= ${yStart}`, sql`${appreciations.awardDate} <= ${yEnd}`];
  if (search) conds.push(like(appreciations.summary, `%${search}%`));
  // Scope filter (reskin-docs/03 §1): Owned → my appreciations (award_to=me), Added → ones I gave, All → company.
  const sc = scopeCondition(req.scope as 1 | 2 | 3 | 4 | 5, req.auth!.userId, { addedBy: appreciations.addedBy, owner: appreciations.awardTo });
  if (sc === "DENY") conds.push(sql`1 = 0`);
  else if (sc) conds.push(sc);
  const sortCol = sortKey === "recipient" ? users.name : sortKey === "badge" ? awards.title : appreciations.awardDate;

  const rows = await db
    .select({
      id: appreciations.id, awardDate: appreciations.awardDate, summary: appreciations.summary,
      awardTitle: awards.title, recipientId: appreciations.awardTo, recipientName: users.name, recipientImage: users.image,
      giverName: giver.name,
    })
    .from(appreciations)
    .leftJoin(awards, eq(awards.id, appreciations.awardId))
    .leftJoin(users, eq(users.id, appreciations.awardTo))
    .leftJoin(giver, eq(giver.id, appreciations.addedBy))
    .where(and(...conds))
    .orderBy(dir(sortCol));

  const asDateStr = (v: unknown) => (v instanceof Date ? v.toISOString().slice(0, 10) : String(v).slice(0, 10));
  const mo = new Date().getMonth();
  const yr = new Date().getFullYear();
  const list = rows.map((r) => ({
    id: r.id, date: asDateStr(r.awardDate), summary: r.summary ?? "",
    award: r.awardTitle ?? "—", recipientId: r.recipientId, recipient: r.recipientName ?? "—", recipientImage: r.recipientImage, givenBy: r.giverName ?? "—",
  }));
  res.json({
    rows: list,
    kpis: {
      thisYear: list.length,
      thisMonth: year === yr ? list.filter((r) => new Date(r.date).getMonth() === mo).length : 0,
      recipients: new Set(list.map((r) => r.recipientId)).size,
      badges: new Set(rows.map((r) => r.awardTitle)).size,
    },
    years: [year - 2, year - 1, year, year + 1],
  });
});

/** GET /api/appreciations/options — awards + employees for the give-award drawer (legacy L57–58). */
hrRouter.get("/appreciations/options", requirePermission("view_appreciation"), async (req, res) => {
  const companyId = req.auth!.companyId;
  const [awardOpts, emps] = await Promise.all([
    db.select({ id: awards.id, title: awards.title }).from(awards).where(eq(awards.companyId, companyId)).orderBy(asc(awards.title)),
    db.select({ id: users.id, name: users.name }).from(users).where(eq(users.companyId, companyId)).orderBy(asc(users.name)),
  ]);
  res.json({ awards: awardOpts, employees: emps });
});

const appreciationBody = z.object({
  award_id: z.coerce.number().int().positive(),
  award_to: z.coerce.number().int().positive(),
  award_date: z.string().min(1),
  summary: z.string().optional(),
});

/** POST /api/appreciations — give award (gate add_appreciation=ALL; legacy AppreciationController store). */
hrRouter.post("/appreciations", requirePermission("add_appreciation"), onlyAll("add_appreciation"), async (req, res) => {
  const p = appreciationBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [r] = await db.insert(appreciations).values({
    companyId: req.auth!.companyId,
    awardId: p.data.award_id,
    awardTo: p.data.award_to,
    awardDate: new Date(p.data.award_date),
    summary: p.data.summary ?? null,
    addedBy: req.auth!.userId,
  }).$returningId();
  res.status(201).json({ id: r!.id, message: "Appreciation given" });
});

/* ── Award badges (the badge TYPES used by Appreciations; legacy AwardController). Managed by the
 *    same admin/HR who can give appreciations (add_appreciation=ALL). Feeds the give-award picker. ── */
hrRouter.get("/awards", requirePermission("view_appreciation"), async (req, res) => {
  const companyId = req.auth!.companyId;
  const rows = await db.select({ id: awards.id, title: awards.title, colorCode: awards.colorCode, status: awards.status, summary: awards.summary }).from(awards).where(eq(awards.companyId, companyId)).orderBy(asc(awards.title));
  const usage = await db.select({ awardId: appreciations.awardId, n: count() }).from(appreciations).where(eq(appreciations.companyId, companyId)).groupBy(appreciations.awardId);
  const used: Record<number, number> = {};
  for (const u of usage) used[Number(u.awardId)] = Number(u.n);
  res.json({ rows: rows.map((a) => ({ ...a, usage: used[a.id] ?? 0 })) });
});

const awardBody = z.object({ title: z.string().trim().min(1).max(255), color_code: z.string().trim().default("#6366f1"), summary: z.string().optional() });
hrRouter.post("/awards", requirePermission("add_appreciation"), onlyAll("add_appreciation"), async (req, res) => {
  const p = awardBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [r] = await db.insert(awards).values({ companyId: req.auth!.companyId, title: p.data.title, colorCode: p.data.color_code || "#6366f1", summary: p.data.summary ?? null, status: "active" }).$returningId();
  res.status(201).json({ id: r!.id, message: "Badge created" });
});
hrRouter.put("/awards/:id", requirePermission("add_appreciation"), onlyAll("add_appreciation"), async (req, res) => {
  const id = Number(req.params.id);
  const p = awardBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [exists] = await db.select({ id: awards.id }).from(awards).where(and(eq(awards.id, id), eq(awards.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.update(awards).set({ title: p.data.title, colorCode: p.data.color_code || "#6366f1", summary: p.data.summary ?? null }).where(eq(awards.id, id));
  res.json({ id, message: "Badge updated" });
});
hrRouter.delete("/awards/:id", requirePermission("delete_appreciation"), onlyAll("delete_appreciation"), async (req, res) => {
  const id = Number(req.params.id);
  const [inUse] = await db.select({ c: count() }).from(appreciations).where(and(eq(appreciations.awardId, id), eq(appreciations.companyId, req.auth!.companyId)));
  if (Number(inUse?.c ?? 0) > 0) return res.status(422).json({ error: "This badge has been given out — can't delete it" });
  await db.delete(awards).where(and(eq(awards.id, id), eq(awards.companyId, req.auth!.companyId)));
  res.json({ id, message: "Badge deleted" });
});

/** DELETE /api/appreciations/:id (gate delete_appreciation=ALL). */
hrRouter.delete("/appreciations/:id", requirePermission("delete_appreciation"), onlyAll("delete_appreciation"), async (req, res) => {
  const id = Number(req.params.id);
  const [exists] = await db.select({ id: appreciations.id }).from(appreciations).where(and(eq(appreciations.id, id), eq(appreciations.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.delete(appreciations).where(eq(appreciations.id, id));
  res.json({ id, message: "Appreciation deleted" });
});

/* ───────────────────────────── Promotions ─────────────────────────────
 * Admin/HR only. The promotion permissions aren't seeded in this tenant, so — matching legacy
 * ("admin OR view_promotion=all") — we gate on view_attendance=ALL (the seesAll / HR gate). Ported
 * from V2 PromotionsController index L20–52. Recording is HISTORY-ONLY (legacy PromotionController@
 * store inserts the row; it does NOT mutate employee_details — PHP wins, flagged). */

const promoSeesAll = [requirePermission("view_attendance"), onlyAll("view_attendance")];

hrRouter.get("/promotions", ...promoSeesAll, async (req, res) => {
  const companyId = req.auth!.companyId;
  const year = Number(req.query.year) || 0;
  const deptId = Number(req.query.department_id) || 0;
  const search = (req.query.search as string | undefined)?.trim() ?? "";
  const dir = (req.query.dir as string) === "asc" ? asc : desc;
  const prevD = alias(designations, "prevD");
  const currD = alias(designations, "currD");
  const prevT = alias(teams, "prevT");
  const currT = alias(teams, "currT");

  const conds: SQL[] = [eq(promotions.companyId, companyId)];
  if (year > 0) conds.push(sql`YEAR(${promotions.date}) = ${year}`);
  if (deptId > 0) conds.push(eq(promotions.currentDepartmentId, deptId));
  if (search) conds.push(like(users.name, `%${search}%`));

  const rows = await db
    .select({
      id: promotions.id, date: promotions.date, employeeId: promotions.employeeId, employee: users.name,
      fromDesignation: prevD.name, toDesignation: currD.name, fromDepartment: prevT.teamName, toDepartment: currT.teamName,
    })
    .from(promotions)
    .leftJoin(users, eq(users.id, promotions.employeeId))
    .leftJoin(prevD, eq(prevD.id, promotions.previousDesignationId))
    .leftJoin(currD, eq(currD.id, promotions.currentDesignationId))
    .leftJoin(prevT, eq(prevT.id, promotions.previousDepartmentId))
    .leftJoin(currT, eq(currT.id, promotions.currentDepartmentId))
    .where(and(...conds))
    .orderBy(dir((req.query.sort as string) === "employee" ? users.name : promotions.date));

  const asDateStr = (v: unknown) => (v == null ? "" : v instanceof Date ? v.toISOString().slice(0, 10) : String(v).slice(0, 10));
  const now = new Date();
  const d30 = new Date(now.getTime() - 30 * 86400000).toISOString().slice(0, 10);
  const list = rows.map((r) => ({
    id: r.id, date: asDateStr(r.date), employeeId: r.employeeId, employee: r.employee ?? "—",
    fromDesignation: r.fromDesignation ?? "—", toDesignation: r.toDesignation ?? "—",
    fromDepartment: r.fromDepartment ?? "—", toDepartment: r.toDepartment ?? "—",
  }));
  res.json({
    rows: list,
    kpis: {
      total: list.length,
      thisYear: list.filter((r) => r.date.startsWith(String(now.getFullYear()))).length,
      last30d: list.filter((r) => r.date >= d30).length,
      employees: new Set(list.map((r) => r.employeeId)).size,
    },
    years: [...new Set(list.map((r) => r.date.slice(0, 4)).filter(Boolean))].sort().reverse(),
  });
});

/** Options for the record-promotion drawer (employees + designations + departments). */
hrRouter.get("/promotions/options", ...promoSeesAll, async (req, res) => {
  const companyId = req.auth!.companyId;
  const [emps, desigs, depts] = await Promise.all([
    db.select({ id: users.id, name: users.name }).from(users).where(eq(users.companyId, companyId)).orderBy(asc(users.name)),
    db.select({ id: designations.id, name: designations.name }).from(designations).where(eq(designations.companyId, companyId)).orderBy(asc(designations.name)),
    db.select({ id: teams.id, name: teams.teamName }).from(teams).where(eq(teams.companyId, companyId)).orderBy(asc(teams.teamName)),
  ]);
  res.json({ employees: emps, designations: desigs, departments: depts });
});

const promoBody = z.object({
  employee_id: z.coerce.number().int().positive(),
  current_designation_id: z.coerce.number().int().positive().optional(),
  current_department_id: z.coerce.number().int().positive().optional(),
  date: z.string().min(1),
});

/** POST /api/promotions — record (history only; snapshots "previous" from the employee's current
 *  employee_details, "current" from input). Does NOT mutate employee_details (legacy parity). */
hrRouter.post("/promotions", ...promoSeesAll, async (req, res) => {
  const p = promoBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [ed] = await db.select({ desig: employeeDetails.designationId, dept: employeeDetails.departmentId }).from(employeeDetails).where(eq(employeeDetails.userId, p.data.employee_id)).limit(1);
  const [r] = await db.insert(promotions).values({
    companyId: req.auth!.companyId,
    employeeId: p.data.employee_id,
    date: new Date(p.data.date),
    previousDesignationId: ed?.desig ?? null,
    currentDesignationId: p.data.current_designation_id ?? ed?.desig ?? null,
    previousDepartmentId: ed?.dept ?? null,
    currentDepartmentId: p.data.current_department_id ?? ed?.dept ?? null,
  }).$returningId();
  res.status(201).json({ id: r!.id, message: "Promotion recorded" });
});

/** DELETE /api/promotions/:id. */
hrRouter.delete("/promotions/:id", ...promoSeesAll, async (req, res) => {
  const id = Number(req.params.id);
  const [exists] = await db.select({ id: promotions.id }).from(promotions).where(and(eq(promotions.id, id), eq(promotions.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.delete(promotions).where(eq(promotions.id, id));
  res.json({ id, message: "Promotion deleted" });
});

/* ───────────────────────────── Compliance items ─────────────────────────────
 * Admin/HR only — gated on view_attendance=ALL (legacy reuses the HR-dashboard gate; there's no
 * dedicated *_compliance permission). Ported from V2 ComplianceItemController L23–176. KPIs reflect
 * company totals (NOT the active filter). last_updated_by written on every mutation. */

const COMPLIANCE_TYPES = ["training", "verification", "review", "medical", "other"] as const;
const compSeesAll = [requirePermission("view_attendance"), onlyAll("view_attendance")];

hrRouter.get("/compliance", ...compSeesAll, async (req, res) => {
  const companyId = req.auth!.companyId;
  const status = (["open", "done", "all"] as const).includes(req.query.status as never) ? (req.query.status as string) : "open";
  const type = COMPLIANCE_TYPES.includes(req.query.type as never) ? (req.query.type as string) : "";
  const search = (req.query.search as string | undefined)?.trim() ?? "";
  const sort = (req.query.sort as string) || "due_asc";

  const conds: SQL[] = [eq(complianceItems.companyId, companyId)];
  if (status === "open") conds.push(sql`${complianceItems.completedAt} is null`);
  if (status === "done") conds.push(sql`${complianceItems.completedAt} is not null`);
  if (type) conds.push(eq(complianceItems.type, type));
  if (search) conds.push(like(complianceItems.title, `%${search}%`));

  const orderBy =
    sort === "due_desc" ? desc(complianceItems.dueDate)
      : sort === "newest" ? desc(complianceItems.id)
      : sort === "oldest" ? asc(complianceItems.id)
      : asc(complianceItems.dueDate);

  const rows = await db
    .select({
      id: complianceItems.id, title: complianceItems.title, description: complianceItems.description, type: complianceItems.type,
      dueDate: complianceItems.dueDate, completedAt: complianceItems.completedAt, ownerId: complianceItems.ownerId, ownerName: users.name,
    })
    .from(complianceItems)
    .leftJoin(users, eq(users.id, complianceItems.ownerId))
    .where(and(...conds))
    .orderBy(orderBy);

  // KPIs = company totals, ignoring the active filter (legacy L50–55).
  const today = todayStr();
  const soon = new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10);
  const all = await db.select({ completedAt: complianceItems.completedAt, dueDate: complianceItems.dueDate }).from(complianceItems).where(eq(complianceItems.companyId, companyId));
  const asD = (v: unknown) => (v instanceof Date ? v.toISOString().slice(0, 10) : String(v).slice(0, 10));
  const open = all.filter((r) => r.completedAt == null);
  res.json({
    rows: rows.map((r) => ({
      id: r.id, title: r.title, description: r.description ?? "", type: r.type, dueDate: asD(r.dueDate),
      done: r.completedAt != null, completedAt: r.completedAt ? asD(r.completedAt) : null, ownerId: r.ownerId, owner: r.ownerName ?? null,
      overdue: r.completedAt == null && asD(r.dueDate) < today,
    })),
    kpis: {
      open: open.length,
      overdue: open.filter((r) => asD(r.dueDate) < today).length,
      dueSoon: open.filter((r) => asD(r.dueDate) >= today && asD(r.dueDate) <= soon).length,
      done: all.length - open.length,
    },
    types: COMPLIANCE_TYPES,
  });
});

hrRouter.get("/compliance/options", ...compSeesAll, async (req, res) => {
  const owners = await db.select({ id: users.id, name: users.name }).from(users).where(and(eq(users.companyId, req.auth!.companyId), sql`${users.status} <> 'deactive'`)).orderBy(asc(users.name));
  res.json({ owners, types: COMPLIANCE_TYPES });
});

const complianceBody = z.object({
  title: z.string().trim().min(1).max(191),
  type: z.enum(COMPLIANCE_TYPES).default("other"),
  due_date: z.string().min(1),
  owner_id: z.coerce.number().int().positive().nullable().optional(),
  description: z.string().optional(),
});

hrRouter.post("/compliance", ...compSeesAll, async (req, res) => {
  const p = complianceBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [r] = await db.insert(complianceItems).values({
    companyId: req.auth!.companyId, title: p.data.title, type: p.data.type, dueDate: new Date(p.data.due_date),
    ownerId: p.data.owner_id ?? null, description: p.data.description ?? null, createdBy: req.auth!.userId, lastUpdatedBy: req.auth!.userId,
  }).$returningId();
  res.status(201).json({ id: r!.id, message: "Compliance item added" });
});

hrRouter.put("/compliance/:id", ...compSeesAll, async (req, res) => {
  const id = Number(req.params.id);
  const p = complianceBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [exists] = await db.select({ id: complianceItems.id }).from(complianceItems).where(and(eq(complianceItems.id, id), eq(complianceItems.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.update(complianceItems).set({
    title: p.data.title, type: p.data.type, dueDate: new Date(p.data.due_date), ownerId: p.data.owner_id ?? null, description: p.data.description ?? null, lastUpdatedBy: req.auth!.userId,
  }).where(eq(complianceItems.id, id));
  res.json({ id, message: "Compliance item updated" });
});

const setDone = (done: boolean) => async (req: import("express").Request, res: import("express").Response) => {
  const id = Number(req.params.id);
  const [exists] = await db.select({ id: complianceItems.id }).from(complianceItems).where(and(eq(complianceItems.id, id), eq(complianceItems.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.update(complianceItems).set({ completedAt: done ? new Date() : null, lastUpdatedBy: req.auth!.userId }).where(eq(complianceItems.id, id));
  res.json({ id, message: done ? "Marked done" : "Reopened" });
};
hrRouter.post("/compliance/:id/done", ...compSeesAll, setDone(true));
hrRouter.post("/compliance/:id/reopen", ...compSeesAll, setDone(false));

hrRouter.delete("/compliance/:id", ...compSeesAll, async (req, res) => {
  const id = Number(req.params.id);
  const [exists] = await db.select({ id: complianceItems.id }).from(complianceItems).where(and(eq(complianceItems.id, id), eq(complianceItems.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.delete(complianceItems).where(eq(complianceItems.id, id));
  res.json({ id, message: "Compliance item deleted" });
});
